import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronDown,
  faChevronRight,
  faEnvelope,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/indexRedux";
import {
  BessResignError,
  fetchBessResignCampaigns,
} from "../../api/bessResign";
import { getProductLabelFromId } from "../../functions/flexServices";
import type {
  BessResignCampaignCompany,
  BessResignCampaignContract,
} from "../../types/bessResignTypes";

type CompanyStatus = "done" | "abandoned" | "inProgress" | "notStarted";

const getCompanyStatus = (c: BessResignCampaignCompany): CompanyStatus => {
  if (c.all_signed) return "done";
  if (c.contracts.some((k) => k.enrolled && !k.signed_at)) return "abandoned";
  if (c.signed_contracts > 0) return "inProgress";
  return "notStarted";
};

const formatDate = (value: string | null, fallback: string): string => {
  if (!value) return fallback;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? fallback : d.toLocaleDateString();
};

const statusStyles: Record<CompanyStatus, string> = {
  done: "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  inProgress: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  abandoned: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  notStarted: "bg-gray-100 text-gray-600 dark:bg-primary-800 dark:text-primary-300",
};

const statusKey: Record<CompanyStatus, string> = {
  done: "bessResignStatusDone",
  inProgress: "bessResignStatusInProgress",
  abandoned: "bessResignStatusAbandoned",
  notStarted: "bessResignStatusNotStarted",
};

const BessResignAdmin: React.FC = () => {
  const { t } = useTranslation("management");
  const navigate = useNavigate();

  const accessToken = useSelector((state: RootState) => state.authv2.accessToken);

  const [companies, setCompanies] = useState<BessResignCampaignCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | CompanyStatus>("all");
  const [productFilter, setProductFilter] = useState<"all" | "PV curtailment" | "BESS">("all");
  const [expanded, setExpanded] = useState<number | null>(null);

  const loadCampaigns = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchBessResignCampaigns(accessToken);
      setCompanies(response.companies ?? []);
    } catch (err) {
      console.error(err);
      if (err instanceof BessResignError) {
        setError(err.message || t("bessResignErrorLoading"));
      } else {
        setError(t("bessResignErrorLoading"));
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, t]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const totals = useMemo(() => {
    const counts = { done: 0, abandoned: 0, inProgress: 0, notStarted: 0 };
    companies.forEach((c) => {
      counts[getCompanyStatus(c)] += 1;
    });
    return counts;
  }, [companies]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return companies.filter((c) => {
      const matchesSearch =
        !query ||
        c.company_name.toLowerCase().includes(query) ||
        String(c.company_number).includes(query) ||
        c.vat_code.toLowerCase().includes(query);
      const matchesStatus = statusFilter === "all" || getCompanyStatus(c) === statusFilter;
      const matchesProduct =
        productFilter === "all" ||
        c.contracts.some((k) => getProductLabelFromId(k.product_id) === productFilter);
      return matchesSearch && matchesStatus && matchesProduct;
    });
  }, [companies, search, statusFilter, productFilter]);

  const contractStatusLabel = (contract: BessResignCampaignContract): { label: string; className: string } => {
    if (contract.signed_at) {
      return {
        label: t("bessResignContractSigned"),
        className: "text-green-600 dark:text-green-400",
      };
    }
    if (contract.enrolled) {
      return {
        label: t("bessResignContractEnrolled"),
        className: "text-amber-600 dark:text-amber-400",
      };
    }
    return {
      label: t("bessResignContractPending"),
      className: "text-primary-400 dark:text-primary-500",
    };
  };

  const renderEmail = (value: string | null) =>
    value ? (
      <a
        href={`mailto:${value}`}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-primary-500 dark:text-primary-300 hover:text-primary-700 dark:hover:text-white break-all"
      >
        <FontAwesomeIcon icon={faEnvelope} className="text-[10px]" />
        {value}
      </a>
    ) : (
      <span className="text-primary-400 dark:text-primary-500">{t("notAvailable")}</span>
    );

  return (
    <div className="my-12 md:mx-14 text-primary-600 dark:text-primary-300">
      <button
        onClick={() => navigate("/management")}
        className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-600 dark:text-primary-300 dark:hover:text-white mb-4 transition-colors"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        {t("backToManagement")}
      </button>
      <h1 className="text-2xl font-semibold dark:text-white mb-1">{t("bessResignManager")}</h1>
      <p className="text-sm text-primary-400 dark:text-primary-300 mb-6">{t("bessResignManagerDescription")}</p>

      {!loading && !error && companies.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {(["done", "inProgress", "abandoned", "notStarted"] as CompanyStatus[]).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter((prev) => (prev === status ? "all" : status))}
              className={`px-3 py-2 rounded-md border text-left transition-colors ${
                statusFilter === status
                  ? "border-primary-500 bg-primary-50 dark:bg-primary-900 dark:border-primary-400"
                  : "border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900 hover:border-primary-300 dark:hover:border-primary-700"
              }`}
            >
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-semibold text-primary-600 dark:text-white">{totals[status]}</span>
                <span className="text-xs text-primary-400 dark:text-primary-300">{t(statusKey[status])}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap mb-3">
        <input
          type="text"
          className="input input-sm input-bordered w-full max-w-sm dark:bg-primary-900"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("bessResignSearchPlaceholder")}
        />
        <select
          className="select select-sm select-bordered dark:bg-primary-900"
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value as typeof productFilter)}
        >
          <option value="all">{t("bessResignProductFilterAll")}</option>
          <option value="PV curtailment">{t("bessResignProductFilterPv")}</option>
          <option value="BESS">{t("bessResignProductFilterBess")}</option>
        </select>
        {statusFilter !== "all" && (
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            className="text-xs font-semibold text-primary-500 hover:text-primary-700 dark:text-primary-300 dark:hover:text-white"
          >
            {t("bessResignClearFilter")}
          </button>
        )}
        <span className="text-xs text-primary-400 dark:text-primary-500 ml-auto">
          {t("bessResignShowing", { count: filtered.length, total: companies.length })}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-primary-400 py-6">
          <FontAwesomeIcon icon={faSpinner} spin />
          {t("loading")}
        </div>
      ) : error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-primary-400 dark:text-primary-500 py-6">
          {companies.length === 0 ? t("bessResignNoCompanies") : t("bessResignNoResults")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900">
          <table className="table table-xs w-full">
            <thead>
              <tr className="text-primary-500 dark:text-primary-300 border-b border-base-200 dark:border-primary-800 text-xs uppercase tracking-wide">
                <th className="w-6"></th>
                <th className="font-semibold">{t("bessResignCompany")}</th>
                <th className="font-semibold">{t("bessResignKbo")}</th>
                <th className="font-semibold">{t("bessResignVat")}</th>
                <th className="font-semibold text-center">{t("status")}</th>
                <th className="font-semibold text-center">{t("bessResignSigned")}</th>
                <th className="font-semibold">{t("bessResignCompanyEmail")}</th>
                <th className="font-semibold">{t("bessResignBillingEmail")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((company) => {
                const status = getCompanyStatus(company);
                const isOpen = expanded === company.company_id;
                return (
                  <React.Fragment key={company.company_id}>
                    <tr
                      className="border-b border-base-200 dark:border-primary-800 text-primary-600 dark:text-primary-300 hover:bg-base-200/40 dark:hover:bg-primary-950 cursor-pointer"
                      onClick={() => setExpanded(isOpen ? null : company.company_id)}
                    >
                      <td className="text-center text-primary-400">
                        <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="text-xs" />
                      </td>
                      <td className="font-medium text-primary-600 dark:text-white">{company.company_name}</td>
                      <td className="font-mono text-xs">{company.company_number}</td>
                      <td className="font-mono text-xs">{company.vat_code}</td>
                      <td className="text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusStyles[status]}`}>
                          {t(statusKey[status])}
                        </span>
                      </td>
                      <td className="text-center font-mono text-xs">
                        {company.signed_contracts}/{company.total_contracts}
                      </td>
                      <td className="text-xs">{renderEmail(company.company_email)}</td>
                      <td className="text-xs">{renderEmail(company.billing_email)}</td>
                    </tr>

                    {isOpen && (
                      <tr className="bg-base-200/30 dark:bg-primary-950">
                        <td colSpan={8} className="p-0">
                          <div className="p-3">
                            <table className="table table-xs w-full">
                              <thead>
                                <tr className="text-primary-500 dark:text-primary-300 border-b border-base-200 dark:border-primary-800">
                                  <th className="font-semibold">{t("bessResignSite")}</th>
                                  <th className="font-semibold">{t("bessResignInstallation")}</th>
                                  <th className="font-semibold">{t("bessResignProduct")}</th>
                                  <th className="font-semibold">{t("bessResignStart")}</th>
                                  <th className="font-semibold">{t("bessResignEnd")}</th>
                                  <th className="font-semibold">{t("bessResignContractStatus")}</th>
                                  <th className="font-semibold">{t("bessResignSignedAt")}</th>
                                  <th className="font-semibold">{t("bessResignSignedBy")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {company.contracts.map((contract) => {
                                  const cs = contractStatusLabel(contract);
                                  return (
                                    <tr
                                      key={contract.contract_id}
                                      className="border-b border-base-200 dark:border-primary-800 text-primary-600 dark:text-primary-300"
                                    >
                                      <td className="text-xs">{contract.site_name ?? t("notAvailable")}</td>
                                      <td className="font-mono text-xs">{contract.installation_id}</td>
                                      <td className="text-xs">{getProductLabelFromId(contract.product_id)}</td>
                                      <td className="text-xs">{formatDate(contract.start_date, t("notAvailable"))}</td>
                                      <td className="text-xs">{formatDate(contract.end_date, "—")}</td>
                                      <td className={`text-xs font-semibold ${cs.className}`}>{cs.label}</td>
                                      <td className="text-xs">{formatDate(contract.signed_at, "—")}</td>
                                      <td className="text-xs break-all">{contract.signed_by_email ?? "—"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BessResignAdmin;
